import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PackageListView, PaginatedPackageListViewList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface QueryKeyType {
  pageParam?: UrlParams;
}

interface IUsePackages {
  search?: string;
  eventTypes?: string[];
  packageCategory?: string;
  packageRevenueCategories?: string[];
  types?: PackageListView['type'][];
  cacheKey?: string;
  contract?: string;
  isArchived?: boolean;
  limit?: string;
  athleteId?: string;
}

function usePackages({
  search,
  eventTypes,
  packageCategory,
  packageRevenueCategories,
  types,
  cacheKey,
  contract,
  isArchived,
  limit = '20',
  athleteId,
}: IUsePackages): UseInfiniteQueryResult<AxiosResponse<PaginatedPackageListViewList>> {
  const fetchPackages = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedPackageListViewList>> => {
      const params = pageParam;
      if (search) {
        params.search = search;
      }
      if (eventTypes && eventTypes.length > 0) {
        params.event_type = eventTypes.join(',');
      }
      if (packageRevenueCategories && packageRevenueCategories.length > 0) {
        params.package_revenue_categories = packageRevenueCategories.join(',');
      }
      if (types && types.length > 0) {
        params.type = types.join(',');
      }
      if (contract) {
        params.contract = contract;
      }
      if (packageCategory) {
        params.package_category = packageCategory;
      }

      if (isArchived) {
        params.is_archived = 'true';
      } else {
        params.is_archived = 'false';
      }

      if (athleteId) {
        params.athlete = athleteId;
      }

      return client.get(`packages/?${encodeUrlParams(pageParam)}`);
    },
    [
      limit,
      search,
      eventTypes,
      contract,
      isArchived,
      packageCategory,
      packageRevenueCategories,
      types,
      athleteId,
    ],
  );

  return useInfiniteQuery(
    [
      'packages',
      search,
      eventTypes,
      cacheKey,
      contract,
      isArchived,
      packageCategory,
      packageRevenueCategories,
      types,
      athleteId,
    ],
    fetchPackages,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
    },
  );
}

export default usePackages;
